'use client';

import type { CodeSessionPullRequest } from '@/lib/code-sessions/pull-request';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { codeSessionKeys, enabledUntilRefused } from './queries';
import { codeSessionRequest, jsonBody } from './runtime/cloud';
import { codeSessionWorkspaceKeys } from './workspace-keys';
import { refreshSessionWorkspace } from './workspace-refresh';

export function codeSessionPullRequestOptions(sessionId: string, enabled = true) {
  return queryOptions({
    // Workspace invalidation already runs after Git actions, terminal turns,
    // and reconnects, including when the agent creates a PR through its shell.
    queryKey: codeSessionWorkspaceKeys.pullRequest(sessionId),
    queryFn: ({ signal }) =>
      codeSessionRequest<CodeSessionPullRequest | null>(
        `/api/code-sessions/${sessionId}/pull-request`,
        { signal },
      ),
    enabled: enabledUntilRefused(enabled),
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

export function useCodeSessionPullRequest(sessionId: string, enabled = true) {
  return useQuery(codeSessionPullRequestOptions(sessionId, enabled));
}

export function useCreateCodeSessionPullRequest(sessionId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: codeSessionKeys.mutation(sessionId, 'create-pr'),
    retry: false,
    mutationFn: (input: { title: string; body: string; ready: boolean }) =>
      codeSessionRequest<CodeSessionPullRequest>(
        `/api/code-sessions/${sessionId}/pull-request`,
        jsonBody(input),
      ),
    onSuccess: async (pullRequest) => {
      const queryKey = codeSessionWorkspaceKeys.pullRequest(sessionId);
      await client.cancelQueries({ queryKey });
      client.setQueryData(queryKey, pullRequest);
    },
    // Keep the POST response authoritative: an immediate GitHub read may still miss the new PR.
    onError: () => refreshSessionWorkspace(client, sessionId),
  });
}
